import React, { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import { BsChevronDoubleLeft } from 'react-icons/bs';

import DefaultModal from 'components/Modal/DefaultModal';
import FlexSelect from 'components/Inputs/FlexSelect';

import { ValuePreview } from '../ValuePreview';

import {
  ActionSection,
  ArrowContainer,
  FieldGroup,
  FieldLabel,
  FieldSelectionWrapper,
  MappingContainer,
  ModalContent,
  PreviewSection,
  SelectWrapper,
  StyledButton
} from './styles';
import { AlertBox } from 'views/CRM/PriceCatalog/externalForm/components/ChangeDetailsModal/styled';

export const AutoMappingModal = ({
  show,
  onToggle,
  control,
  setValue,
  availableCustomFields = [],
  availablePrevCustomFields = [],
  selectedFieldInfo,
  autoMappingPrevField,
  availablePrevFieldsForMapping = [],
  parseValuesByType
}) => {
  const canApplyMapping = useMemo(() => {
    return selectedFieldInfo?.name && autoMappingPrevField;
  }, [selectedFieldInfo?.name, autoMappingPrevField]);

  const handleApplyMapping = () => {
    if (!canApplyMapping) {
      toast.error('Por favor, selecione um campo para mapeamento automático.', {
        autoClose: 3000
      });
      return;
    }

    const prevField = availablePrevCustomFields.find(
      (field) => field.id === autoMappingPrevField
    );

    if (!prevField) {
      toast.error(
        'Campo selecionado não encontrado no tipo de registro atual.',
        { autoClose: 1000 }
      );
      return;
    }

    try {
      const name = selectedFieldInfo.name;
      const value =
        parseValuesByType?.(prevField.value, prevField.type) ?? prevField.value;

      setValue(name, value);

      toast.success(
        `Campo "${prevField.label || prevField.name}" mapeado com sucesso!`,
        {
          autoClose: 900
        }
      );
    } catch (error) {
      console.error('Erro ao mapear campo:', error);
      toast.error('Erro ao mapear campo. Tente novamente.', {
        autoClose: 3000
      });
    }
  };

  const getNewFieldPlaceholder = () => {
    if (availableCustomFields.length === 0) {
      return 'Nenhum campo disponível para mapeamento';
    }
    return 'Selecione os campos para mapeamento automático';
  };

  const getCurrentFieldPlaceholder = () => {
    if (
      selectedFieldInfo?.name !== '' &&
      availablePrevFieldsForMapping.length === 0
    ) {
      return 'Não há campos no registro anterior para mapear';
    }
    if (!selectedFieldInfo?.name) {
      return 'Selecione um campo do novo registro';
    }
    return 'Selecione campos do registro atual';
  };

  return (
    <DefaultModal
      show={show}
      onModalToggle={onToggle}
      title="Mapeamento Automático"
      subtitle="Configure ou revise o mapeamento automático dos campos"
      modalContainerClasses="modal-lg"
    >
      <ModalContent>
        <AlertBox type="info">
          <strong>Instruções: </strong>
          Você pode selecionar os campos que deseja mapear automaticamente para
          o novo tipo de registro. O mapeamento automático tentará alinhar os
          campos do tipo de registro atual com os campos do novo tipo de
          registro.
        </AlertBox>

        <MappingContainer>
          <FieldSelectionWrapper>
            <FieldGroup>
              <FieldLabel>Campos do Novo Registro</FieldLabel>
              <SelectWrapper>
                <Controller
                  name="autoMappingFields"
                  control={control}
                  defaultValue=""
                  render={(props) => (
                    <FlexSelect
                      dataOptions={availableCustomFields.map(
                        ({ label, id }) => ({
                          label,
                          value: id
                        })
                      )}
                      value={props.value}
                      onChange={props.onChange}
                      valueController={setValue}
                      fieldName="autoMappingFields"
                      labelName="label"
                      valueName="value"
                      isClearable
                      closeMenuOnSelect={false}
                      isDisabled={availableCustomFields.length === 0}
                      placeholder={getNewFieldPlaceholder()}
                    />
                  )}
                />
              </SelectWrapper>
            </FieldGroup>

            <ArrowContainer>
              <BsChevronDoubleLeft size={28} />
            </ArrowContainer>

            <FieldGroup>
              <FieldLabel>Campos do Registro Atual</FieldLabel>
              <SelectWrapper>
                <Controller
                  name="autoMappingPrevFields"
                  control={control}
                  defaultValue=""
                  render={(props) => (
                    <FlexSelect
                      dataOptions={availablePrevFieldsForMapping}
                      value={props.value}
                      onChange={props.onChange}
                      valueController={setValue}
                      fieldName="autoMappingPrevFields"
                      labelName="label"
                      valueName="value"
                      isClearable
                      closeMenuOnSelect={false}
                      isDisabled={availablePrevFieldsForMapping.length === 0}
                      placeholder={getCurrentFieldPlaceholder()}
                    />
                  )}
                />
              </SelectWrapper>
            </FieldGroup>
          </FieldSelectionWrapper>

          {selectedFieldInfo?.name && autoMappingPrevField && (
            <PreviewSection>
              <ValuePreview
                selectedFieldInfo={selectedFieldInfo}
                autoMappingPrevField={autoMappingPrevField}
                availablePrevCustomFields={availablePrevCustomFields}
                parseValuesByType={parseValuesByType}
              />
            </PreviewSection>
          )}

          <ActionSection>
            <StyledButton
              color="primary"
              onClick={handleApplyMapping}
              disabled={!canApplyMapping}
            >
              {canApplyMapping ? 'Aplicar Mapeamento' : 'Selecione os Campos'}
            </StyledButton>
          </ActionSection>
        </MappingContainer>
      </ModalContent>
    </DefaultModal>
  );
};
